import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useClassroomDay } from '../../hooks/useClassroomDay.js';
import { FireworkBurst } from './FireworkBurst.js';
import styles from './Question.module.css';

const DOUBLE_TAP_WINDOW_MS = 600;

export function Question() {
  const api = useApi();
  const { active } = useActiveClassroom();
  // Show flags + the weekly question stay in settings; the DAILY question +
  // answer come from today's per-day record (carry-forward).
  const settings = useQuery({
    queryKey: ['classroom-settings', active?.id ?? 'none'],
    queryFn: () => api.home.getSettings(active!.id),
    enabled: active !== null,
  });
  const day = useClassroomDay(active?.id);

  const [revealedDaily, setRevealedDaily] = useState(false);
  const [revealedWeekly, setRevealedWeekly] = useState(false);
  const [burst, setBurst] = useState<{ x: number; y: number; key: number } | null>(null);
  const lastTap = useRef<{ which: 'daily' | 'weekly'; t: number } | null>(null);

  // Reset reveal whenever the classroom or page enters fresh.
  useEffect(() => {
    setRevealedDaily(false);
    setRevealedWeekly(false);
  }, [active?.id]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  function tap(which: 'daily' | 'weekly', e: { clientX: number; clientY: number }) {
    const now = Date.now();
    if (
      lastTap.current &&
      lastTap.current.which === which &&
      now - lastTap.current.t < DOUBLE_TAP_WINDOW_MS
    ) {
      // Double-tap detected — reveal + fire the firework burst.
      if (which === 'daily') setRevealedDaily(true);
      else setRevealedWeekly(true);
      setBurst({ x: e.clientX, y: e.clientY, key: now });
      lastTap.current = null;
    } else {
      lastTap.current = { which, t: now };
    }
  }

  const showDaily = settings.data?.questionShowDaily ?? false;
  const showWeekly = settings.data?.questionShowWeekly ?? false;

  return (
    <section className={styles.question} data-testid="question-page">
      {!showDaily && !showWeekly ? (
        <p className={styles.placeholder} data-testid="question-empty">
          Question of the Day / Week aren&rsquo;t turned on yet — flip them on in the Home settings
          panel.
        </p>
      ) : null}

      {showDaily ? (
        <article
          role="button"
          tabIndex={0}
          className={styles.card}
          data-testid="question-daily"
          onClick={(e) => tap('daily', e)}
        >
          <h2>Question of the Day</h2>
          <p className={styles.prompt}>{day.data?.questionDaily || '—'}</p>
          {revealedDaily ? (
            <p className={styles.answer} data-testid="question-daily-answer">
              {day.data?.questionDailyAnswer || '—'}
            </p>
          ) : (
            <p className={styles.hint}>Double-tap to reveal the answer.</p>
          )}
        </article>
      ) : null}

      {showWeekly ? (
        <article
          role="button"
          tabIndex={0}
          className={styles.card}
          data-testid="question-weekly"
          onClick={(e) => tap('weekly', e)}
        >
          <h2>Question of the Week</h2>
          <p className={styles.prompt}>{settings.data?.questionWeekly || '—'}</p>
          {revealedWeekly ? (
            <p className={styles.answer} data-testid="question-weekly-answer">
              {settings.data?.questionWeeklyAnswer || '—'}
            </p>
          ) : (
            <p className={styles.hint}>Double-tap to reveal the answer.</p>
          )}
        </article>
      ) : null}

      {burst ? <FireworkBurst x={burst.x} y={burst.y} key={burst.key} /> : null}
    </section>
  );
}
